import { useMutation, useQuery } from '@apollo/client/react';
import {
  AddOutlined,
  CheckCircleOutline,
  Close,
  DeleteOutline,
  DescriptionOutlined,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  LocalShippingOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { titleize } from 'inflection';
import React, { useContext, useState } from 'react';

import CollectedModal from '../ReportComponents/DeliveryRegister/CollectedModal';
import DeliveryModal from '../ReportComponents/DeliveryRegister/DeliveryModal';
import DeployModal from '../ReportComponents/DeliveryRegister/DeployModal';
import EditDeliveryModal from '../ReportComponents/DeliveryRegister/EditDeliveryModal';
import DiarySingleEntry from '../ReportComponents/DiaryEntry/CalendarView/DiarySingleEntry';
import RequestPermissionTooltip from '../RequestPermissionTooltip';
import RequestAccessBlock from '../RequestPermissionTooltip/RequestAccessBlock';

import CurrentUserContext from '@/Contexts/CurrentUser';
import PermissionContext from '@/Contexts/Permissions';
import { ReportContext } from '@/Contexts/ReportContext';
import {
  type DeliveryAndCollectionItemState,
  DeliveryAndCollectionItemStateEnum,
  type DeliveryAndCollectionRegisterItem,
  DestroyDeliveryAndCollectionItemDocument,
  GetDeliveryItemStatesDocument,
  type Issue,
  PermissionActionEnum,
  PermissionEntityEnum,
} from '@/graphql/generated/graphql';
import { displayNotification } from '@/utils/notifications';

type LogisticsFilter = 'all' | 'arrived' | 'collection';

interface DeliveryCardV2Props {
  state: DeliveryAndCollectionItemState;
  uuid: string;
  onItemAction: (action: 'deploy' | 'collect', itemState: DeliveryAndCollectionItemState) => void;
}

const DeliveryCardV2: React.FC<DeliveryCardV2Props> = ({ state, uuid, onItemAction }) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMoreDetails, setShowMoreDetails] = useState(false);

  const deliveryItem: DeliveryAndCollectionRegisterItem = state.item;
  const isStateArrived = state.currentState === DeliveryAndCollectionItemStateEnum.Arrived;
  const canDelete = canPerformAction(PermissionActionEnum.Manage, PermissionEntityEnum.Scheduling);
  const canEdit = canPerformAction(PermissionActionEnum.Edit, PermissionEntityEnum.Scheduling);

  const [destroyDeliveryAndCollectionItem] = useMutation(DestroyDeliveryAndCollectionItemDocument, {
    onCompleted: () => {
      displayNotification({
        text: 'Delivery And Collection Item deleted successfully',
        type: 'success',
      });
    },
    onError: (error) => {
      displayNotification({ text: error.message, type: 'error' });
    },
  });

  const handleDeleteItem = (id: string) => {
    destroyDeliveryAndCollectionItem({
      variables: { id },
      update(cache) {
        cache.evict({
          id: cache.identify({
            __typename: 'DeliveryAndCollectionRegisterItem',
            id,
          }),
        });
        cache.gc();
      },
    });
  };

  const accentColor = isStateArrived ? '#1D4ED8' : '#D97706';
  const badgeBg = isStateArrived ? '#EFF6FF' : '#FFFBEB';
  const badgeBorder = isStateArrived ? '#BFDBFE' : '#FDE68A';
  const badgeText = isStateArrived ? '#1E40AF' : '#B45309';

  return (
    <Box
      sx={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: 1.5,
        p: 1.5,
        mb: 1.25,
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          borderColor: '#CBD5E1',
          borderLeftColor: accentColor,
          boxShadow: '0 2px 6px rgba(15, 23, 42, 0.05)',
        },
      }}
    >
      {/* Top Row: Equipment Name, Short Code, Qty + Status Pill */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: '#0F172A',
                fontSize: '0.85rem',
              }}
            >
              {deliveryItem.companyResource?.name}
            </Typography>
            {deliveryItem.companyResource?.resourceMeta?.shortCode && (
              <Chip
                label={deliveryItem.companyResource.resourceMeta.shortCode}
                size="small"
                sx={{
                  height: 18,
                  fontFamily: 'monospace',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  backgroundColor: '#F1F5F9',
                  color: '#334155',
                  borderRadius: 0.75,
                }}
              />
            )}
            <Chip
              label={`Qty: ${deliveryItem.quantity}`}
              size="small"
              sx={{
                height: 18,
                fontFamily: 'monospace',
                fontSize: '0.68rem',
                fontWeight: 700,
                backgroundColor: '#F8FAFC',
                color: '#0F172A',
                border: '1px solid #E2E8F0',
                borderRadius: 0.75,
              }}
            />
          </Stack>
        </Box>

        <Chip
          label={`${titleize(state.currentState)} · ${dayjs(state.currentStateSetAt).format(dateTimePreference.time)}`}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.68rem',
            fontWeight: 700,
            backgroundColor: badgeBg,
            color: badgeText,
            border: `1px solid ${badgeBorder}`,
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        />
      </Stack>

      {/* Always-Visible 3-Column Stakeholder Strip (0 Clicks Required) */}
      <Grid
        container
        spacing={1}
        sx={{
          mt: 1.25,
          p: 1,
          borderRadius: 1,
          backgroundColor: '#F8FAFC',
          border: '1px solid #F1F5F9',
        }}
      >
        <Grid size={4}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: '0.62rem',
              fontWeight: 700,
              color: '#64748B',
              letterSpacing: '0.04em',
            }}
          >
            SUPPLIER
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: 'block',
              fontWeight: 600,
              color: '#0F172A',
              fontSize: '0.75rem',
            }}
          >
            {state.supplier || '—'}
          </Typography>
        </Grid>
        <Grid size={4}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: '0.62rem',
              fontWeight: 700,
              color: '#64748B',
              letterSpacing: '0.04em',
            }}
          >
            DELIVERER
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: 'block',
              fontWeight: 600,
              color: '#0F172A',
              fontSize: '0.75rem',
            }}
          >
            {state.delivererName || '—'}
          </Typography>
        </Grid>
        <Grid size={4}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: '0.62rem',
              fontWeight: 700,
              color: '#64748B',
              letterSpacing: '0.04em',
            }}
          >
            RECEIVER
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: 'block',
              fontWeight: 600,
              color: '#0F172A',
              fontSize: '0.75rem',
            }}
          >
            {state.receiverName || '—'}
          </Typography>
        </Grid>
      </Grid>

      {/* Inline Notes Callout (if present) */}
      {state.notes && (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="flex-start"
          sx={{
            mt: 1,
            px: 1,
            py: 0.75,
            borderRadius: 1,
            backgroundColor: '#FFFBEB',
            border: '1px solid #FDE68A',
          }}
        >
          <DescriptionOutlined sx={{ fontSize: 14, color: '#B45309', mt: 0.25 }} />
          <Typography variant="caption" sx={{ color: '#92400E', fontSize: '0.72rem', lineHeight: 1.4 }}>
            {state.notes}
          </Typography>
        </Stack>
      )}

      {/* Expandable Technical Metadata */}
      <Collapse in={showMoreDetails}>
        <Box
          sx={{
            mt: 1,
            p: 1,
            borderRadius: 1,
            backgroundColor: '#F8FAFC',
            border: '1px dashed #CBD5E1',
          }}
        >
          <Grid container spacing={1}>
            <Grid size={6}>
              <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.65rem', display: 'block' }}>
                CATEGORY
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#0F172A' }}>
                {deliveryItem.companyResource?.resourceMeta?.resourceCategory?.name || '—'}
              </Typography>
            </Grid>
            <Grid size={6}>
              <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.65rem', display: 'block' }}>
                DELIVERY UUID
              </Typography>
              <Typography
                variant="caption"
                noWrap
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.68rem',
                  color: '#334155',
                  display: 'block',
                }}
              >
                {uuid}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      {/* 1-Click Action Footer */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 1.25, pt: 1, borderTop: '1px solid #F1F5F9' }}
      >
        <Button
          size="small"
          variant="text"
          endIcon={showMoreDetails ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
          onClick={() => setShowMoreDetails((prev) => !prev)}
          sx={{
            height: 24,
            px: 0.75,
            textTransform: 'none',
            fontSize: '0.7rem',
            fontWeight: 600,
            color: '#64748B',
          }}
        >
          {showMoreDetails ? 'Hide ID' : 'Details'}
        </Button>

        <Stack direction="row" alignItems="center" spacing={0.75}>
          <RequestPermissionTooltip
            hasPermission={canEdit}
            requiredPermission={{
              action: PermissionActionEnum.Edit,
              entity: PermissionEntityEnum.Scheduling,
            }}
            checkRequestExists={checkRequestExists}
            onRequest={requestPermission}
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  isStateArrived ? (
                    <LocalShippingOutlined sx={{ fontSize: 14 }} />
                  ) : (
                    <CheckCircleOutline sx={{ fontSize: 14 }} />
                  )
                }
                disabled={!canEdit}
                onClick={() => onItemAction(isStateArrived ? 'deploy' : 'collect', state)}
                sx={{
                  height: 26,
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  borderRadius: 1,
                  px: 1.25,
                  borderColor: isStateArrived ? '#BFDBFE' : '#FDE68A',
                  backgroundColor: isStateArrived ? '#EFF6FF' : '#FFFBEB',
                  color: isStateArrived ? '#1D4ED8' : '#B45309',
                  '&:hover': {
                    backgroundColor: isStateArrived ? '#DBEAFE' : '#FEF3C7',
                    borderColor: isStateArrived ? '#93C5FD' : '#FCD34D',
                  },
                }}
              >
                {isStateArrived ? 'Mark Deployed' : 'Mark Collected'}
              </Button>
            </span>
          </RequestPermissionTooltip>

          <RequestPermissionTooltip
            hasPermission={canEdit}
            requiredPermission={{
              action: PermissionActionEnum.Edit,
              entity: PermissionEntityEnum.Scheduling,
            }}
            checkRequestExists={checkRequestExists}
            onRequest={requestPermission}
          >
            <span>
              <Tooltip title={canEdit ? 'Edit Delivery Entry' : ''}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!(canEdit && isStateArrived)}
                    onClick={() => setShowEditModal(true)}
                    sx={{
                      width: 26,
                      height: 26,
                      border: '1px solid #E2E8F0',
                      borderRadius: 1,
                    }}
                  >
                    <EditOutlined sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </span>
          </RequestPermissionTooltip>

          <RequestPermissionTooltip
            hasPermission={canDelete}
            requiredPermission={{
              action: PermissionActionEnum.Manage,
              entity: PermissionEntityEnum.Scheduling,
            }}
            checkRequestExists={checkRequestExists}
            onRequest={requestPermission}
          >
            <span>
              <Tooltip title={canDelete ? 'Remove Delivery Entry' : ''}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!(canDelete && isStateArrived)}
                    onClick={() => handleDeleteItem(deliveryItem.id)}
                    sx={{
                      width: 26,
                      height: 26,
                      border: '1px solid #E2E8F0',
                      borderRadius: 1,
                      color: canDelete && isStateArrived ? '#DC2626' : 'action.disabled',
                    }}
                  >
                    <DeleteOutline sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </span>
          </RequestPermissionTooltip>
        </Stack>
      </Stack>

      {showEditModal && (
        <EditDeliveryModal handleClose={() => setShowEditModal(false)} deliveryItem={deliveryItem} itemState={state} />
      )}
    </Box>
  );
};

interface DeliveryRegisterV2Props {
  selectedDiaryEntry: Issue | null;
  setSelectedDiaryEntry: React.Dispatch<React.SetStateAction<Issue | null>>;
}

const DeliveryRegisterV2: React.FC<DeliveryRegisterV2Props> = ({ selectedDiaryEntry, setSelectedDiaryEntry }) => {
  const { selectedReport } = useContext(ReportContext);
  const { canPerformAction, checkRequestExists, requestPermission } = useContext(PermissionContext);

  const [filterMode, setFilterMode] = useState<LogisticsFilter>('all');
  const [deliveryModal, toggleDeliveryModal] = useState<boolean>(false);
  const [deployedModal, toggleDeployedModal] = useState<boolean>(false);
  const [collectedModal, toggleCollectedModal] = useState<boolean>(false);
  const [reportCurrentState, setReportCurrentState] = useState<DeliveryAndCollectionItemState | undefined>(undefined);

  const { data } = useQuery(GetDeliveryItemStatesDocument, {
    fetchPolicy: 'cache-and-network',
    skip: !selectedReport,
    variables: { reportId: selectedReport?.id as string },
  });

  const onItemAction = (action: 'deploy' | 'collect', itemState: DeliveryAndCollectionItemState) => {
    if (action === 'deploy') {
      toggleDeployedModal(true);
    }
    if (action === 'collect') {
      toggleCollectedModal(true);
    }
    setReportCurrentState(itemState);
  };

  // Active items exclude already deployed or collected states
  const activeStates = (data?.getDeliveryItemStates || []).filter(
    (s) =>
      s &&
      ![DeliveryAndCollectionItemStateEnum.Deployed, DeliveryAndCollectionItemStateEnum.Collected].includes(
        s.currentState,
      ),
  ) as DeliveryAndCollectionItemState[];

  const arrivedStates = activeStates.filter((s) => s.currentState === DeliveryAndCollectionItemStateEnum.Arrived);
  const collectionStates = activeStates.filter(
    (s) => s.currentState === DeliveryAndCollectionItemStateEnum.ReadyForCollection,
  );

  const displayedStates =
    filterMode === 'arrived' ? arrivedStates : filterMode === 'collection' ? collectionStates : activeStates;

  const canCreateDelivery = canPerformAction(PermissionActionEnum.Create, PermissionEntityEnum.Scheduling);
  const canViewDelivery = canPerformAction(PermissionActionEnum.View, PermissionEntityEnum.Scheduling);

  // If a Diary Entry is selected from the timeline or table, show the 1-Click Inline Inspector
  if (selectedDiaryEntry) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: '#FFFFFF',
          border: '1px solid #1D4ED8',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            backgroundColor: '#EFF6FF',
            borderBottom: '1px solid #BFDBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                color: '#1E3A8A',
                letterSpacing: '0.05em',
                display: 'block',
              }}
            >
              DIARY ENTRY INSPECTOR
            </Typography>
            <Typography variant="caption" sx={{ color: '#3B82F6' }}>
              Editing selected timeline / log entry
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Close fontSize="small" />}
            onClick={() => setSelectedDiaryEntry(null)}
            sx={{
              height: 28,
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              borderColor: '#93C5FD',
              color: '#1E3A8A',
              backgroundColor: '#FFFFFF',
            }}
          >
            Back to Deliveries
          </Button>
        </Box>
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          <DiarySingleEntry
            key={selectedDiaryEntry.id}
            selectedDiaryEntry={selectedDiaryEntry}
            setSelectedDiaryEntry={setSelectedDiaryEntry}
          />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E2E8F0',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Corporate Panel Header */}
      <Box
        sx={{
          px: 2,
          py: 1.125,
          backgroundColor: '#F8FAFC',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: '#0F172A',
              letterSpacing: '0.05em',
              fontSize: '0.74rem',
            }}
          >
            DELIVERIES & COLLECTIONS
          </Typography>
          <Chip
            label={activeStates.length}
            size="small"
            sx={{
              height: 20,
              fontWeight: 700,
              fontSize: '0.7rem',
              backgroundColor: '#E2E8F0',
              color: '#0F172A',
            }}
          />
        </Stack>

        <RequestPermissionTooltip
          hasPermission={canCreateDelivery}
          requiredPermission={{
            action: PermissionActionEnum.Create,
            entity: PermissionEntityEnum.Scheduling,
          }}
          checkRequestExists={checkRequestExists}
          onRequest={requestPermission}
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddOutlined fontSize="small" />}
              disabled={!canCreateDelivery}
              onClick={() => toggleDeliveryModal(true)}
              sx={{
                height: 30,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.76rem',
                borderRadius: 1.5,
                borderColor: '#CBD5E1',
                color: '#0F172A',
                backgroundColor: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#F1F5F9',
                  borderColor: '#94A3B8',
                },
              }}
            >
              Log Delivery
            </Button>
          </span>
        </RequestPermissionTooltip>
      </Box>

      {/* 1-Click Filter Sub-Bar */}
      <Box
        sx={{
          px: 2,
          py: 0.75,
          backgroundColor: '#FFFFFF',
          borderBottom: '1px solid #F1F5F9',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        {(
          [
            { key: 'all', label: 'All Active', count: activeStates.length },
            { key: 'arrived', label: 'Arrived', count: arrivedStates.length },
            {
              key: 'collection',
              label: 'For Collection',
              count: collectionStates.length,
            },
          ] as const
        ).map((pill) => {
          const isSelected = filterMode === pill.key;
          return (
            <Chip
              key={pill.key}
              label={`${pill.label} (${pill.count})`}
              size="small"
              onClick={() => setFilterMode(pill.key)}
              sx={{
                height: 22,
                fontSize: '0.68rem',
                fontWeight: isSelected ? 700 : 500,
                cursor: 'pointer',
                borderRadius: 1,
                backgroundColor: isSelected ? '#0F172A' : '#F8FAFC',
                color: isSelected ? '#FFFFFF' : '#475569',
                border: '1px solid',
                borderColor: isSelected ? '#0F172A' : '#E2E8F0',
              }}
            />
          );
        })}
      </Box>

      {/* Scrollable Logistics Card List */}
      {!canViewDelivery ? (
        <Box sx={{ p: 2 }}>
          <RequestAccessBlock
            requiredPermission={{
              action: PermissionActionEnum.View,
              entity: PermissionEntityEnum.Scheduling,
            }}
            checkRequestExists={checkRequestExists}
            onRequest={requestPermission}
          />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            maxHeight: 515,
            overflowY: 'auto',
            p: 1.5,
            backgroundColor: '#F8FAFC',
          }}
        >
          {displayedStates.length > 0 ? (
            displayedStates.map((state, index) => (
              <DeliveryCardV2
                key={`${state.id}-${index}`}
                uuid={state.uuid}
                state={state}
                onItemAction={onItemAction}
              />
            ))
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
                py: 6,
                px: 2,
                textAlign: 'center',
                border: '1px dashed #CBD5E1',
                borderRadius: 2,
                backgroundColor: '#FFFFFF',
              }}
            >
              <LocalShippingOutlined sx={{ fontSize: 32, color: '#94A3B8' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                No Active Deliveries or Collections
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', maxWidth: 240 }}>
                Log incoming site deliveries or mark equipment as ready for collection from the Equipment Register.
              </Typography>
            </Stack>
          )}
        </Box>
      )}

      <Divider />

      {/* Modals */}
      {deliveryModal && <DeliveryModal handleClose={() => toggleDeliveryModal(false)} />}
      {deployedModal && reportCurrentState && (
        <DeployModal
          handleClose={() => {
            toggleDeployedModal(false);
            setReportCurrentState(undefined);
          }}
          stateId={reportCurrentState.id}
        />
      )}
      {collectedModal && reportCurrentState && (
        <CollectedModal
          handleClose={() => {
            toggleCollectedModal(false);
            setReportCurrentState(undefined);
          }}
          stateId={reportCurrentState.id}
        />
      )}
    </Paper>
  );
};

export default DeliveryRegisterV2;
